/**
 * UI2a(DESIGN 14.441) — **위쪽 가운데 알림 줄.** 이야기 띠(`#world-story`)·도착 띠(`#story-arrival`)·사냥터 넘기기 알약(`#field-shift`)·
 * 다시 잇는 중 알약(`#link.on.quiet`)·전체 공지(`#ann`)가 모두 화면 위쪽 가운데 `top:8px` 에 저마다 떠서, 첫 입장(이야기 + 넘기기가
 * 동시에 뜨는 순간)에 글자끼리 겹쳤다(평론 2차 N4). 이 모듈은 그 자리를 **두 줄짜리 대기열**로 묶는다 — 알림을 띄우는 쪽 코드는
 * 그대로 두고(각자 `.on`·글자만 바꾼다) 여기서 보고 자리를 정한다.
 *
 * - 1줄(띠): 이야기 또는 도착 띠. 있으면 맨 위다(읽던 글이 밀려 내려가지 않게).
 * - 2줄(상태 한 칸): 띠 바로 아래(띠가 없으면 맨 위) **한 칸**. 우선순위 다시 잇는 중 > 사냥터 넘기기 > 잠깐 알림(시야 단계 등) > 공지.
 *   - 병합: 다시 잇는 중과 넘기기가 함께 켜지면 한 알약(다시 잇는 중)만 선다 — 둘 다 「잠깐 기다려 달라」라는 한 말이다.
 *   - 최소 표시: 알약은 한 번 서면 `PILL_MIN_MS` 동안은 원래 쪽이 꺼도 남는다(0.2초 번쩍임이 고장으로 읽히지 않게).
 *   - 대기: 알약이 칸을 쓰는 동안 공지는 숨어 기다리고(공지 문장은 기록에 이미 남아 있다), 칸이 비면 제 시간 안에서 다시 선다.
 * - 3줄(제자리 요소): 상태 칩(광고 부스트 · 피해 측정)과 공지는 제자리에 서되, 위의 띠·알약과 겹치면 그 아래로 내려 쌓는다.
 * 알약은 이 모듈의 알약 하나(`#lane-pill`)가 대신 선다 — 원래 알약은 보이지 않게만 둔다(`html.lane-on`). 세로 폰처럼 띠가 아래에 있는
 * 배치에서는 띠를 건드리지 않는다(위쪽 칸에 겹칠 것이 없다). 「가로로 돌려 달라」 안내(`#landscape-hint.on`)가 덮는 동안은 알약도 서지 않는다
 * (다시 잇는 중 알약은 원래 게임 층 안이라 안내 밑에 가려졌다 — 넘기기 알약도 같게 둔다).
 */
export const PILL_MIN_MS = 1000;
export const LANE_GAP = 6;
const TOP = 8;
/** 위쪽 가운데에 인라인 top 으로 서는 상태 칩(hud.js — 광고 부스트 남은 시간 · 피해 측정 창). 띠·알약과 겹칠 때만 그 아래로 내린다. */
const CHIPS = ['adboost-chip', 'dps-chip'];

let pill = null, shownAt = 0, lastText = '', lastKind = '', note = null, noteTimer = 0, raf = 0, holdTimer = 0, mo = null, hintSeen = false;

const $ = (id) => document.getElementById(id);
const visible = (el) => !!el && el.getClientRects().length > 0 && getComputedStyle(el).display !== 'none';

/** 지금 칸을 원하는 알림 — { kind, text } 또는 null. 순수 판정은 `pickLane`. */
export function pickLane({ linkQuiet, linkText, shift, shiftText, shiftCue = '', noteText, now, shownAt: at, lastKind: lk, lastText: lt }) {
  if (linkQuiet) return { kind: 'link', text: linkText || '다시 잇는 중…' };
  // C9(14.476) — 이동 표시의 까닭(`reason` — 도는 표시 없이)·다시 잇기(`retry` — 눌러서 다시 잇는다)는 알약 종류로 가른다
  if (shift) return { kind: shiftCue === 'reason' ? 'reason' : shiftCue === 'retry' ? 'retry' : 'shift', text: shiftText || '사냥터를 잇는 중…' };
  if (lk && lk !== 'note' && now - at < PILL_MIN_MS) return { kind: lk, text: lt, held: true };
  if (noteText) return { kind: 'note', text: noteText };
  return null;
}

function ensurePill() {
  if (pill) return pill;
  pill = document.createElement('div');
  pill.id = 'lane-pill';
  pill.setAttribute('role', 'status');
  pill.setAttribute('aria-live', 'polite');
  pill.innerHTML = '<i></i><span></span>';
  // C9(14.476) — 「연결 확인 중 — 눌러서 다시 잇기」 알약은 누를 수 있다(창 대신 — 관문 3). 이동 표시(crossing-cue.js)가 듣는다
  pill.addEventListener('click', () => { if (pill.dataset.kind === 'retry') dispatchEvent(new CustomEvent('unige:crossing-retry')); });
  // 원래 넘기기 알약 바로 뒤(같은 층·같은 순서) — 뒤에 붙는 말풍선(#tip)·덮개가 전처럼 알약 위에 선다
  const shift = $('field-shift');
  if (shift?.parentNode) shift.after(pill); else document.body.appendChild(pill);
  return pill;
}

function layout() {
  raf = 0;
  const link = $('link'), shift = $('field-shift'), story = $('world-story'), arrival = $('story-arrival'), ann = $('ann');
  const linkOn = !!link?.classList.contains('on');
  const linkQuiet = linkOn && link.classList.contains('quiet');
  const now = performance.now();
  const want = pickLane({
    linkQuiet, linkText: $('linkmsg')?.textContent?.trim(),
    shift: !!shift?.classList.contains('on') && !(linkOn && !linkQuiet), shiftText: shift?.querySelector('span')?.textContent?.trim(), shiftCue: shift?.dataset.cue || '',
    noteText: note?.text, now, shownAt, lastKind, lastText,
  });
  // 1줄 — 위쪽에 선 띠(세로 폰은 띠가 아래라 위쪽 칸과 안 겹친다)
  let bandBottom = 0, band = null;
  for (const el of [story, arrival]) {
    if (!el?.classList.contains('on') || !visible(el)) continue;
    const r = el.getBoundingClientRect();
    if (r.top < innerHeight / 2 && r.bottom > bandBottom) { bandBottom = r.bottom; band = r; }
  }
  const slotTop = bandBottom ? Math.round(bandBottom + LANE_GAP) : TOP;
  // 「가로로 돌려 달라」 안내(처음 필요할 때 만들어진다 — 그때부터 켜짐을 지켜본다)가 덮는 동안은 알약을 세우지 않는다
  const hint = $('landscape-hint');
  if (hint && !hintSeen && mo) { hintSeen = true; mo.observe(hint, { attributes: true, attributeFilter: ['class'] }); }
  const covered = !!hint?.classList.contains('on');
  // 2줄 — 상태 한 칸
  const p = ensurePill();
  if (want && !(linkOn && !linkQuiet) && !covered) {
    if (!want.held && (want.kind !== lastKind || !p.classList.contains('on'))) shownAt = now;
    if (!want.held) { lastKind = want.kind; lastText = want.text; }
    p.querySelector('span').textContent = want.text;
    p.dataset.kind = want.kind;
    p.style.top = `${slotTop}px`;
    p.classList.add('on');
    if (want.held) { clearTimeout(holdTimer); holdTimer = setTimeout(schedule, Math.max(0, PILL_MIN_MS - (now - shownAt)) + 20); }
  } else {
    p.classList.remove('on');
    if (!want) lastKind = '';
  }
  // 3줄 — 위쪽 가운데에 저마다 서던 상태 칩(광고 부스트 · 피해 측정, hud.js 가 인라인 top 으로 세운다)과 공지는 제자리에 두되,
  // 이미 선 띠·알약과 겹치면 그 아래로 내려 쌓는다(가로로 겹치는 것들의 아래 끝 + 간격)
  const placed = [];
  if (band) placed.push(band);
  if (p.classList.contains('on')) { const r = p.getBoundingClientRect(); placed.push({ left: r.left, right: r.right, top: slotTop, bottom: slotTop + p.offsetHeight }); }
  const stack = (el, restore) => {
    restore();
    if (!visible(el) || el.hidden) return;
    let r = el.getBoundingClientRect();
    const over = placed.filter((q) => q.left < r.right && r.left < q.right);
    if (over.some((q) => q.top < r.bottom && r.top < q.bottom)) {
      const top = Math.round(Math.max(...over.map((q) => q.bottom)) + LANE_GAP);
      el.style.top = `${top}px`;
      r = { left: r.left, right: r.right, top, bottom: top + r.height };
    }
    placed.push(r);
  };
  for (const id of CHIPS) {
    const el = $(id);
    if (!el) continue;
    if (!('laneTop0' in el.dataset)) { el.dataset.laneTop0 = el.style.top || ''; mo?.observe(el, { attributes: true, attributeFilter: ['hidden'] }); }
    stack(el, () => { el.style.top = el.dataset.laneTop0; });
  }
  // 공지 — 알약이 칸을 쓰는 동안은 숨어 기다린다(제 타이머는 hud.js 가 그대로 센다 · 문장은 기록에 이미 남아 있다). 휴대폰에서 공지가
  // 왼쪽 HUD 아래에 붙는 배치는 겹칠 것이 없으면 그대로 둔다
  if (ann) {
    const busy = p.classList.contains('on');
    ann.classList.toggle('lane-wait', busy && !!ann.textContent.trim());
    if (!busy && ann.textContent.trim()) stack(ann, () => ann.style.removeProperty('top'));
    else ann.style.removeProperty('top');
  }
  document.documentElement.classList.add('lane-on');
}

/** 다음 틈에 한 번 — 그리기 프레임(rAF)을 기다리지 않는다: 느린 기기에서 알약이 프레임 하나(수백 ms) 늦게 서며 겹쳐 보이던 틈을 없앤다. */
export function schedule() { if (!raf) raf = setTimeout(layout, 0); }

/** 잠깐 알림 한 줄(시야 단계 등) — 알약보다 뒤, 공지보다 앞. */
export function laneNote(text, ms = 1200) {
  note = { text: String(text || '') };
  clearTimeout(noteTimer);
  noteTimer = setTimeout(() => { note = null; schedule(); }, ms);
  schedule();
}

let ready = false;
export function initTopLane() {
  if (ready || typeof document === 'undefined') return;
  ready = true;
  mo = new MutationObserver(schedule);
  for (const id of ['link', 'field-shift', 'world-story', 'story-arrival', 'ann', 'linkmsg']) {
    const el = $(id);
    if (el) mo.observe(el, { attributes: true, attributeFilter: ['class', 'hidden'], childList: true, characterData: true, subtree: true });
  }
  const game = $('game');
  if (game) mo.observe(game, { childList: true });   // 가로 안내 등 게임 층에 새로 붙는 덮개(직계 자식만 — 드물다)
  addEventListener('resize', schedule);
  addEventListener('unige:lane-note', (e) => laneNote(e.detail?.text, e.detail?.ms));
  schedule();
}
